import {
  ArrowRight,
  CheckCircle,
  FileCsv,
  FileXls,
  GitDiff,
  LockKey,
  MagnifyingGlass,
  ShieldCheck,
} from '@phosphor-icons/react'

interface LandingPageProps {
  onLoadDemo: () => void
  onStart: () => void
}

const previewRows = [
  { code: '010401001001', name: '现浇混凝土基础', status: '工程量变化', tone: 'changed' },
  { code: '010502001003', name: '矩形柱', status: '综合单价变化', tone: 'changed' },
  { code: '011201001006', name: '墙面一般抹灰', status: '新增', tone: 'added' },
  { code: '010101003002', name: '挖沟槽土方', status: '待确认', tone: 'pending' },
] as const

function HeroDiffPreview() {
  return (
    <div className="hero-preview" aria-label="差异结果界面预览">
      <div className="hero-preview__topbar">
        <div>
          <span>版本差异</span>
          <strong>招标清单 A / 修订清单 B</strong>
        </div>
        <span className="hero-preview__privacy">
          <LockKey aria-hidden="true" weight="fill" />
          本地处理
        </span>
      </div>
      <div className="hero-preview__summary" aria-hidden="true">
        <div>
          <span>新增</span>
          <strong>2</strong>
        </div>
        <div>
          <span>删除</span>
          <strong>1</strong>
        </div>
        <div>
          <span>修改</span>
          <strong>4</strong>
        </div>
        <div>
          <span>待确认</span>
          <strong>1</strong>
        </div>
      </div>
      <div className="hero-preview__search" aria-hidden="true">
        <MagnifyingGlass weight="regular" />
        搜索编码、名称或项目特征
      </div>
      <div className="hero-preview__rows">
        {previewRows.map((row) => (
          <div className="hero-preview__row" key={row.code}>
            <span className={`status-bar status-bar--${row.tone}`} aria-hidden="true" />
            <code>{row.code}</code>
            <span>{row.name}</span>
            <em className={`status-label status-label--${row.tone}`}>{row.status}</em>
          </div>
        ))}
      </div>
    </div>
  )
}

export function LandingPage({ onLoadDemo, onStart }: LandingPageProps) {
  return (
    <main className="landing-page">
      <section className="hero-section" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">LOCAL-FIRST · OPEN SOURCE</p>
          <h1 id="hero-title">
            工程量清单的 <span>Git Diff</span>
          </h1>
          <p className="hero-lede">
            在浏览器本地对比两版清单 Excel，定位增删项、字段变化、金额影响和疑似错配。
          </p>
          <div className="hero-actions">
            <button
              className="button button--primary button--large"
              type="button"
              onClick={onStart}
            >
              开始对比
              <ArrowRight aria-hidden="true" weight="bold" />
            </button>
            <button
              className="button button--secondary button--large"
              data-testid="load-demo"
              type="button"
              onClick={onLoadDemo}
            >
              加载演示数据
            </button>
          </div>
          <div className="privacy-callout">
            <ShieldCheck aria-hidden="true" weight="duotone" />
            <div>
              <strong>文件仅在本机浏览器处理，不会上传</strong>
              <span>刷新页面或清空数据后，工作簿内容会从内存释放。</span>
            </div>
          </div>
        </div>
        <HeroDiffPreview />
      </section>

      <section className="format-band" aria-label="文件支持范围">
        <div className="format-band__lead">
          <GitDiff aria-hidden="true" weight="duotone" />
          <span>让每一版清单变化都有迹可循。</span>
        </div>
        <div className="format-band__items">
          <span>
            <FileXls aria-hidden="true" /> .xlsx
          </span>
          <span>
            <FileCsv aria-hidden="true" /> .csv
          </span>
          <span>单文件不超过 25MB</span>
          <span>单表不超过 30,000 行</span>
        </div>
      </section>

      <section className="workflow-section" aria-labelledby="workflow-title">
        <div className="section-heading">
          <h2 id="workflow-title">从两份清单到可追溯报告</h2>
          <p>先建立可靠映射，再处理不确定项，最后导出复核底稿。</p>
        </div>
        <ol className="workflow-line">
          <li>
            <span className="workflow-icon">
              <FileXls aria-hidden="true" weight="duotone" />
            </span>
            <div>
              <strong>导入与映射</strong>
              <p>分别选择工作表，确认标题行和七个核心字段。</p>
            </div>
          </li>
          <li>
            <span className="workflow-icon">
              <MagnifyingGlass aria-hidden="true" weight="duotone" />
            </span>
            <div>
              <strong>匹配与复核</strong>
              <p>按唯一编码、重复编码、特征和模糊候选分阶段匹配。</p>
            </div>
          </li>
          <li>
            <span className="workflow-icon">
              <CheckCircle aria-hidden="true" weight="duotone" />
            </span>
            <div>
              <strong>查看与导出</strong>
              <p>筛选字段级差异，核算金额影响，生成格式化 Excel。</p>
            </div>
          </li>
        </ol>
      </section>

      <section className="boundary-section" aria-labelledby="boundary-title">
        <div>
          <h2 id="boundary-title">机械识别负责找变化，专业判断仍由你完成</h2>
          <p>
            BOQ Diff 不读取商业软件专有工程文件，不判断计价合规，也不直接修改原始清单。旧版 .xls
            请先用 Excel 或 WPS 另存为 .xlsx。
          </p>
        </div>
        <ul className="boundary-list">
          <li>
            <CheckCircle aria-hidden="true" weight="fill" />
            完全虚构的演示数据
          </li>
          <li>
            <CheckCircle aria-hidden="true" weight="fill" />
            无账号、后端与遥测
          </li>
          <li>
            <CheckCircle aria-hidden="true" weight="fill" />
            MIT License 开源
          </li>
        </ul>
      </section>

      <footer className="landing-footer">
        <span>BOQ Diff by KanadeK</span>
        <span>GB/T 50500-2024 仅作为术语和业务背景参考，不代表官方认证。</span>
      </footer>
    </main>
  )
}
